import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import InfoPanel from "@/pages/Dashboard/InfoPanel";
import SessionPanel from "@/pages/Dashboard/SessionPanel";
import ConfigPanel from "@/pages/Dashboard/ConfigPanel";
import MembershipPanel from "@/pages/Dashboard/MembershipPanel";
import RecordsPanel from "@/pages/Dashboard/RecordsPanel";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import api from "@/api/axios";
import { ArrowRight, BadgeDollarSign, CircleHelp, ClipboardList, LayoutDashboard, Link2, LogOut, Menu, Settings2, X } from "lucide-react";
import telegramLogo from "@/assets/telegram.svg";
import ThemeToggle from "@/components/ThemeToggle";
import "./UserDashboard.css";

const tabs = [
  { id: "info", label: "账户概览", icon: LayoutDashboard },
  { id: "session", label: "帐号连接", icon: Link2 },
  { id: "config", label: "打卡配置", icon: Settings2 },
  { id: "records", label: "打卡记录", icon: ClipboardList },
  { id: "membership", label: "会员中心", icon: BadgeDollarSign },
] as const;
type TabType = typeof tabs[number]["id"];

export default function DashboardLayout() {
  const { token, setToken, initialized } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("info");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const reduceMotion = useReducedMotion();
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleContentUpdate = () => queryClient.invalidateQueries({ queryKey: ["contents"] });
    window.addEventListener("contentUpdated", handleContentUpdate);
    return () => window.removeEventListener("contentUpdated", handleContentUpdate);
  }, [queryClient]);

  useEffect(() => {
    if (initialized && !token) navigate("/login");
  }, [initialized, token, navigate]);

  const invalidateAll = () => {
    ["userInfo", "sessionStatus", "groups", "selectedGroups", "contents", "miscConfig"].forEach((key) => {
      queryClient.invalidateQueries({ queryKey: [key] });
    });
  };

  const { data: userInfo, isLoading: loadingUser, isError: userError } = useQuery({
    queryKey: ["userInfo"],
    queryFn: async () => (await api.get("/api/userinfo")).data.data,
    enabled: initialized && !!token,
  });
  const { data: sessionStatus, isLoading: loadingSession, isError: sessionError } = useQuery({
    queryKey: ["sessionStatus"],
    queryFn: async () => (await api.get("/api/check_session")).data.session_available ? "connected" : "disconnected",
    enabled: initialized && !!token,
  });
  const { data: groups } = useQuery({
    queryKey: ["groups"],
    queryFn: async () => (await api.get("/api/get_telegram_groups", { timeout: 20000 })).data.groups,
    enabled: sessionStatus === "connected",
  });
  const { data: selectedGroups } = useQuery({
    queryKey: ["selectedGroups"],
    queryFn: async () => (await api.get("/api/get_selected_groups")).data.groups.map((group: any) => group.chat_id),
    enabled: sessionStatus === "connected",
  });
  const { data: contents } = useQuery({
    queryKey: ["contents"],
    queryFn: async () => (await api.get("/api/get_contents")).data.contents,
    enabled: initialized && !!token,
  });
  const { data: miscConfig } = useQuery({
    queryKey: ["miscConfig"],
    queryFn: async () => (await api.get("/api/get_misc_config")).data.config,
    enabled: initialized && !!token,
  });

  const currentTab = tabs.find((tab) => tab.id === activeTab)!;
  const loading = !initialized || loadingUser || loadingSession;
  const logout = () => {
    document.cookie = "token=; path=/; max-age=0";
    setToken(null);
    navigate("/login");
  };

  return (
    <div className="user-shell">
      {sidebarOpen && <button className="user-sidebar-backdrop" aria-label="关闭导航" onClick={() => setSidebarOpen(false)} />}
      <aside className={`user-sidebar ${sidebarOpen ? "is-open" : ""}`}>
        <div className="user-brand"><span className="user-brand-mark"><img src={telegramLogo} alt="" /></span><span><strong>TG Checkin</strong></span></div>
        <nav className="user-nav" aria-label="用户中心导航">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button key={id} className={`user-nav-item ${activeTab === id ? "is-active" : ""}`} onClick={() => { setActiveTab(id); setSidebarOpen(false); }} aria-current={activeTab === id ? "page" : undefined}>
              <Icon size={18} strokeWidth={1.9} /><span>{label}</span>{activeTab === id && <motion.span layoutId="user-nav-indicator" className="user-nav-indicator" transition={{ duration: reduceMotion ? 0 : 0.22 }} />}
            </button>
          ))}
        </nav>
        <div className="user-sidebar-bottom"><Link to="/help" className="user-sidebar-link"><CircleHelp size={17} /> 使用指南 <ArrowRight size={15} /></Link></div>
      </aside>

      <div className="user-workspace">
        <header className="user-topbar">
          <button className="user-menu-button" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label={sidebarOpen ? "关闭导航" : "打开导航"}>{sidebarOpen ? <X size={21} /> : <Menu size={21} />}</button>
          <div className="user-breadcrumb"><span>个人中心</span><span className="user-breadcrumb-divider">/</span><strong>{currentTab.label}</strong></div>
          <div className="user-topbar-actions"><span className={`user-connection ${sessionStatus === "connected" ? "is-connected" : ""}`}><i />{sessionStatus === "connected" ? "Telegram 已连接" : "Telegram 未连接"}</span><ThemeToggle /><span className="user-avatar">{(userInfo?.user_nickname || "用").slice(0, 1)}</span><button onClick={logout} className="user-logout" title="退出登录" aria-label="退出登录"><LogOut size={18} /></button></div>
        </header>
        <main className="user-main">
          {loading ? <div className="user-loading"><span className="user-loading-spinner" />正在加载个人中心...</div> : userError || sessionError || !userInfo || !sessionStatus ? (
            <div className="user-card user-empty"><h2>暂时无法加载个人中心</h2><p>请检查网络连接或稍后重试。</p><button className="user-button user-button-primary" onClick={invalidateAll}>重新加载</button></div>
          ) : <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={activeTab}
              className="user-page-stage"
              role="region"
              aria-label={currentTab.label}
              initial={reduceMotion ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduceMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: -6 }}
              transition={{ duration: reduceMotion ? 0 : 0.2, ease: "easeOut" }}
            >
              <div className="user-page-heading"><h1>{currentTab.label}</h1></div>
              {activeTab === "info" && <InfoPanel data={userInfo} refresh={invalidateAll} onNavigate={(tab) => setActiveTab(tab)} sessionConnected={sessionStatus === "connected"} selectedCount={selectedGroups?.length ?? 0} />}
              {activeTab === "session" && <SessionPanel status={sessionStatus} refresh={invalidateAll} />}
              {activeTab === "config" && (sessionStatus !== "connected" ? <div className="user-card user-empty"><Link2 size={30} /><h2>先连接 Telegram 账号</h2><p>连接成功后，即可管理打卡内容、目标群组和执行时间。</p><button className="user-button user-button-primary" onClick={() => setActiveTab("session")}>前往帐号连接 <ArrowRight size={16} /></button></div> : contents && miscConfig && groups && selectedGroups ? <ConfigPanel contents={contents} miscConfig={miscConfig} groups={groups} selectedGroups={selectedGroups} refresh={invalidateAll} /> : <div className="user-card user-empty">配置加载中，请稍候...</div>)}
              {activeTab === "records" && <RecordsPanel />}
              {activeTab === "membership" && <MembershipPanel />}
            </motion.div>
          </AnimatePresence>}
        </main>
      </div>
    </div>
  );
}
